import { useState, useEffect, useRef } from "react";
import Footer from "../partials/Footer";
import Header from "../partials/Header";
import SuspenseFallback from "../SuspenseFallback/SuspenseFallback";
import { Outlet, useLocation } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { initSessionTimeout, clearSessionTimeout, resetSessionTimeout, SESSION_TIMEOUT_WARNING_EVENT_NAME, SESSION_TIMEOUT_LOGOUT_EVENT_NAME, SESSION_TIMEOUT_MINUTES } from "app/utils/sessionTimeout.service";
import { getSessionUserInformation } from "app/utils/utility.service";
import { doLogout } from "app/views/sessions/session.service";
import "./SessionTimeoutDialog.scss";


const DefaultLayout = (props) => {

    const [visible, setVisible] = useState(false);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [countdownSeconds, setCountdownSeconds] = useState(60);
    const countdownIntervalRef = useRef(null);

    // SECURITY: Initialize session timeout for authenticated users
    // Re-run when pathname changes so we start timeout after login (effect ran first on signin when user was null)
    const location = useLocation();
    useEffect(() => {
        const userInfo = getSessionUserInformation();
        if (userInfo && userInfo.token) {
            initSessionTimeout();
        } else {
            clearSessionTimeout();
        }

        // Listen for warning event from sessionTimeout service
        const onWarn = (e) => {
            const remainingMs = e?.detail?.remainingMs ?? 60 * 1000;
            const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
            setCountdownSeconds(seconds);
            setShowTimeoutWarning(true);

            // Start countdown display (UI only)
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
            countdownIntervalRef.current = setInterval(() => {
                setCountdownSeconds((s) => Math.max(0, s - 1));
            }, 1000);
        };
        // When automatic timeout runs, close the warning dialog immediately
        const onTimeoutLogout = () => {
            setShowTimeoutWarning(false);
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
        };

        window.addEventListener(SESSION_TIMEOUT_WARNING_EVENT_NAME, onWarn);
        window.addEventListener(SESSION_TIMEOUT_LOGOUT_EVENT_NAME, onTimeoutLogout);

        // Cleanup on unmount or before re-run
        return () => {
            clearSessionTimeout();
            window.removeEventListener(SESSION_TIMEOUT_WARNING_EVENT_NAME, onWarn);
            window.removeEventListener(SESSION_TIMEOUT_LOGOUT_EVENT_NAME, onTimeoutLogout);
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
        };
    }, [location.pathname]);

    const handleStayLoggedIn = () => {
        // Treat as activity: reset timers; server will also update on next request.
        resetSessionTimeout();
        setShowTimeoutWarning(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
    };

    const handleLogoutNow = async () => {
        setShowTimeoutWarning(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        clearSessionTimeout();
        try {
            await doLogout();
        } finally {
            window.location.href = "/#/";
        }
    };

    const handleSidebar = () => {
        setVisible(!visible);
    }

    const renderHeader = () => {
            
        return <Header handleSidebar={handleSidebar} />
    }

    // let items = [
    //     {
    //         label: "Home",
    //         command: () => {
    //             window.location.hash = `/`;
    //         },
    //     },
    //     {
    //         label: "My Applications",
    //         command: () => {
    //             window.location.hash = `/my-applications`;
    //         },
    //     },
    //     {
    //         label: "My Profile",
    //         command: () => {
    //             window.location.hash = `/my-profile`;
    //         },
    //     },
    //     {
    //         label: "My Documents",
    //         command: () => {
    //             window.location.hash = `/my-documents`;
    //         },
    //     },
    //     {
    //         label: "My Qualifcation",
    //         command: () => {
    //             window.location.hash = `/my-qualification`;
    //         },
    //     },
    //     {
    //         label: "My Experience",
    //         command: () => {
    //             window.location.hash = `/my-experience`;
    //         },
    //     },
    //     {
    //         label: "Logout",
    //         command: () => {
    //             // dispatch(clearHomePurchaseValues());
    //             sessionStorage.clear();
    //             // navigate("/");
    //         },
    //     },
    // ];

    return (
        <SuspenseFallback>
            <div className="App-container">
                {/* SECURITY/UX: Warn user shortly before automatic logout due to inactivity.
                    Server-side timeout enforcement remains authoritative. */}
                <Dialog
                    header={
                        <div className="session-timeout-header">
                            <i className="pi pi-exclamation-triangle session-timeout-icon"></i>
                            <span>Session Expiring Soon</span>
                        </div>
                    }
                    visible={showTimeoutWarning}
                    onHide={() => setShowTimeoutWarning(false)}
                    closable={false}
                    className="session-timeout-dialog"
                    style={{ width: "32rem" }}
                    modal={true}
                    footer={
                        <div className="session-timeout-footer">
                            <Button
                                label="Stay Logged In"
                                icon="pi pi-check"
                                className="p-button-success p-button-lg"
                                onClick={handleStayLoggedIn}
                                style={{ minWidth: "160px" }}
                            />
                            <Button
                                label="Logout Now"
                                icon="pi pi-sign-out"
                                className="p-button-outlined p-button-lg"
                                onClick={handleLogoutNow}
                                style={{ minWidth: "140px" }}
                            />
                        </div>
                    }
                >
                    <div className="session-timeout-content">
                        <div className="session-timeout-message">
                            <p className="session-timeout-text">
                                Your session will expire after {SESSION_TIMEOUT_MINUTES} minutes of inactivity in
                            </p>
                            <div className="session-timeout-countdown">
                                <div className="countdown-circle">
                                    <span className="countdown-number">{countdownSeconds}</span>
                                    <span className="countdown-label">seconds</span>
                                </div>
                            </div>
                            <ProgressBar 
                                value={(countdownSeconds / 60) * 100} 
                                showValue={false}
                                className="session-timeout-progress"
                            />
                            <p className="session-timeout-hint">
                                Click <strong>"Stay Logged In"</strong> to continue your session
                            </p>
                        </div>
                    </div>
                </Dialog>

                {/* <Sidebar visible={visible} onHide={() => setVisible(false)} position="right" >
                <MegaMenu model={items} orientation="vertical" breakpoint="767px" />

                </Sidebar> */}
                <div className="outer-container">
                    <header className="mb-2 mt-2">
                        {renderHeader()}
                    </header>
                    <main className="container-fluid">
                        <div className={useLocation().pathname.indexOf('reports') == -1 && 'child-outlet'} >
                            <Outlet />
                        </div>
                    </main>
                    <footer>
                        <Footer />
                    </footer>
                </div>
            </div>
        </SuspenseFallback>
    );
};

export default DefaultLayout;
